import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getUserLeagues, getUserRankInLeague } from '../services/leagueService';
import { updateUserProfile } from '../services/userService';
import {
  getPendingDeletion,
  cancelAccountDeletion,
} from '../services/accountService';
import CreateLeagueModal from '../components/CreateLeagueModal';
import './LeagueSelector.css';

export default function LeagueSelector() {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const forceShow = !!location.state?.showSelector;

  const [leagues, setLeagues] = useState(null);
  const [ranks, setRanks] = useState({});     // leagueId → rank number
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  // Account deletion state
  const [pendingDeletion, setPendingDeletion] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);

  const loadLeagues = useCallback((skipAutoRedirect = false) => {
    if (!currentUser) return;
    setError('');
    getUserLeagues(currentUser.uid)
      .then((data) => {
        if (data.length === 1 && !skipAutoRedirect) {
          navigate(`/league/${data[0].id}`, { replace: true });
          return;
        }
        setLeagues(data);

        // Fetch rank for each league in parallel
        Promise.all(
          data.map((l) =>
            getUserRankInLeague(l.id, currentUser.uid).then((rank) => [l.id, rank])
          )
        ).then((pairs) => setRanks(Object.fromEntries(pairs)));
      })
      .catch(() => {
        setError('Failed to load your leagues. Please refresh.');
        setLeagues([]);
      });
  }, [currentUser, navigate]);

  useEffect(() => { loadLeagues(forceShow); }, [loadLeagues, forceShow]);

  useEffect(() => {
    if (!currentUser) return;
    getPendingDeletion(currentUser.uid).then(setPendingDeletion);
  }, [currentUser]);

  const displayName = userProfile?.username || '';
  const isDark = !!userProfile?.darkMode;

  async function toggleDarkMode() {
    if (!currentUser) return;
    await updateUserProfile(currentUser.uid, { darkMode: !isDark });
  }

  async function handleCancelDeletion() {
    setCancelLoading(true);
    try {
      await cancelAccountDeletion(currentUser.uid);
      setPendingDeletion(null);
      setLeagues(null);
      setRanks({});
      loadLeagues();
    } finally {
      setCancelLoading(false);
    }
  }

  if (leagues === null && !error) {
    return (
      <div className="selector-page">
        <div className="selector-loading">Loading your leagues…</div>
      </div>
    );
  }

  // Amendment 14: zero-league users get a dedicated Get Started screen
  if (leagues && leagues.length === 0 && !error) {
    return (
      <div className="selector-page">
        <header className="selector-header">
          <span className="selector-logo display-font">World Cup Pick'em</span>
          <div className="selector-header-actions">
            <button className="selector-icon-btn" onClick={toggleDarkMode} title="Toggle dark mode" aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}>
              {isDark
                ? <svg viewBox="0 0 16 16" width="15" height="15" fill="currentColor" aria-hidden="true"><path d="M8 1a1 1 0 0 1 1 1v1a1 1 0 0 1-2 0V2a1 1 0 0 1 1-1zm0 10a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm6-3a1 1 0 0 1-1 1h-1a1 1 0 0 1 0-2h1a1 1 0 0 1 1 1zM3 8a1 1 0 0 1-1 1H1a1 1 0 0 1 0-2h1a1 1 0 0 1 1 1zm9.19-4.78a1 1 0 0 1 0 1.42l-.71.7a1 1 0 1 1-1.41-1.41l.7-.71a1 1 0 0 1 1.42 0zM5.63 11.8a1 1 0 0 1 0 1.41l-.7.71a1 1 0 1 1-1.42-1.42l.71-.7a1 1 0 0 1 1.41 0zm6.29 1.41a1 1 0 0 1-1.41 0l-.71-.7a1 1 0 0 1 1.41-1.42l.71.71a1 1 0 0 1 0 1.41zM5.63 4.22a1 1 0 0 1-1.41 0l-.71-.7A1 1 0 0 1 4.92 2.1l.71.71a1 1 0 0 1 0 1.41z"/></svg>
                : <svg viewBox="0 0 16 16" width="15" height="15" fill="currentColor" aria-hidden="true"><path d="M6 .278a.77.77 0 0 1 .08.858 7.208 7.208 0 0 0-.878 3.46c0 4.021 3.278 7.277 7.318 7.277q.792-.001 1.533-.16a.787.787 0 0 1 .81.316.733.733 0 0 1-.031.893A8.349 8.349 0 0 1 8.344 16C3.734 16 0 12.286 0 7.71 0 4.266 2.114 1.312 5.124.06A.752.752 0 0 1 6 .278z"/></svg>
              }
            </button>
            <button className="selector-icon-btn" onClick={() => navigate('/account')} title="My account" aria-label="My account">
              <svg viewBox="0 0 16 16" width="15" height="15" fill="currentColor" aria-hidden="true"><circle cx="8" cy="5.5" r="2.8"/><path d="M2 14.5c0-3.3 2.7-6 6-6s6 2.7 6 6H2z"/></svg>
            </button>
            {userProfile?.role === 'admin' && (
              <button className="selector-icon-btn" onClick={() => navigate('/admin')} title="Admin console" aria-label="Admin console">
                <svg viewBox="0 0 16 16" width="15" height="15" fill="currentColor" aria-hidden="true"><path d="M8 4.754a3.246 3.246 0 1 0 0 6.492 3.246 3.246 0 0 0 0-6.492M5.754 8a2.246 2.246 0 1 1 4.492 0 2.246 2.246 0 0 1-4.492 0"/><path d="M9.796 1.343c-.527-1.79-3.065-1.79-3.592 0l-.094.319a.873.873 0 0 1-1.255.52l-.292-.16c-1.64-.892-3.433.902-2.54 2.541l.159.292a.873.873 0 0 1-.52 1.255l-.319.094c-1.79.527-1.79 3.065 0 3.592l.319.094a.873.873 0 0 1 .52 1.255l-.16.292c-.892 1.64.901 3.434 2.541 2.54l.292-.159a.873.873 0 0 1 1.255.52l.094.319c.527 1.79 3.065 1.79 3.592 0l.094-.319a.873.873 0 0 1 1.255-.52l.292.16c1.64.893 3.434-.902 2.54-2.541l-.159-.292a.873.873 0 0 1 .52-1.255l.319-.094c1.79-.527 1.79-3.065 0-3.592l-.319-.094a.873.873 0 0 1-.52-1.255l.16-.292c.893-1.64-.902-3.433-2.541-2.54l-.292.159a.873.873 0 0 1-1.255-.52zm-2.633.283c.246-.835 1.428-.835 1.674 0l.094.319a1.873 1.873 0 0 0 2.693 1.115l.291-.16c.764-.415 1.6.42 1.184 1.185l-.159.292a1.873 1.873 0 0 0 1.116 2.692l.318.094c.835.246.835 1.428 0 1.674l-.319.094a1.873 1.873 0 0 0-1.115 2.693l.16.291c.415.764-.42 1.6-1.185 1.184l-.291-.159a1.873 1.873 0 0 0-2.693 1.116l-.094.318c-.246.835-1.428.835-1.674 0l-.094-.319a1.873 1.873 0 0 0-2.692-1.115l-.292.16c-.764.415-1.6-.42-1.184-1.185l.159-.291A1.873 1.873 0 0 0 1.945 8.93l-.319-.094c-.835-.246-.835-1.428 0-1.674l.319-.094A1.873 1.873 0 0 0 3.06 4.377l-.16-.292c-.415-.764.42-1.6 1.185-1.184l.292.159a1.873 1.873 0 0 0 2.692-1.115z"/></svg>
              </button>
            )}
          </div>
        </header>
        <div className="selector-body">
          {pendingDeletion && <DeletionBanner pendingDeletion={pendingDeletion} onCancel={handleCancelDeletion} cancelLoading={cancelLoading} />}
          <div className="selector-getstarted">
            <div className="selector-getstarted-icon">
              <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 2a10 10 0 0 1 6.32 2.26M12 2a10 10 0 0 0-6.32 2.26M2.26 8.5h4.1l2.32-3.02M21.74 8.5h-4.1l-2.32-3.02M2 12.5l3.5-1 1.5 4.5M22 12.5l-3.5-1-1.5 4.5M8 21l1-4.5h6L16 21M12 2v3.48M5.76 4.26l2.6 3.22M18.24 4.26l-2.6 3.22"/>
              </svg>
            </div>
            <h1 className="selector-getstarted-title">
              {displayName ? `Welcome, ${displayName}!` : 'Welcome!'}
            </h1>
            <p className="selector-getstarted-sub">
              You're not in any leagues yet. Create your own or join a public one to start picking.
            </p>
            <div className="selector-actions">
              <button className="selector-btn-primary" onClick={() => setShowCreate(true)}>
                + Create a League
              </button>
              <button className="selector-btn-secondary" onClick={() => navigate('/directory')}>
                Browse Public Leagues
              </button>
            </div>
          </div>
        </div>
        {showCreate && (
          <CreateLeagueModal
            onClose={() => {
              setShowCreate(false);
              setLeagues(null);
              setRanks({});
              loadLeagues();
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div className="selector-page">
      <header className="selector-header">
        <span className="selector-logo display-font">World Cup Pick'em</span>
        <div className="selector-header-actions">
          <button className="selector-icon-btn" onClick={toggleDarkMode} title="Toggle dark mode" aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}>
            {isDark
              ? <svg viewBox="0 0 16 16" width="15" height="15" fill="currentColor" aria-hidden="true"><path d="M8 1a1 1 0 0 1 1 1v1a1 1 0 0 1-2 0V2a1 1 0 0 1 1-1zm0 10a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm6-3a1 1 0 0 1-1 1h-1a1 1 0 0 1 0-2h1a1 1 0 0 1 1 1zM3 8a1 1 0 0 1-1 1H1a1 1 0 0 1 0-2h1a1 1 0 0 1 1 1zm9.19-4.78a1 1 0 0 1 0 1.42l-.71.7a1 1 0 1 1-1.41-1.41l.7-.71a1 1 0 0 1 1.42 0zM5.63 11.8a1 1 0 0 1 0 1.41l-.7.71a1 1 0 1 1-1.42-1.42l.71-.7a1 1 0 0 1 1.41 0zm6.29 1.41a1 1 0 0 1-1.41 0l-.71-.7a1 1 0 0 1 1.41-1.42l.71.71a1 1 0 0 1 0 1.41zM5.63 4.22a1 1 0 0 1-1.41 0l-.71-.7A1 1 0 0 1 4.92 2.1l.71.71a1 1 0 0 1 0 1.41z"/></svg>
              : <svg viewBox="0 0 16 16" width="15" height="15" fill="currentColor" aria-hidden="true"><path d="M6 .278a.77.77 0 0 1 .08.858 7.208 7.208 0 0 0-.878 3.46c0 4.021 3.278 7.277 7.318 7.277q.792-.001 1.533-.16a.787.787 0 0 1 .81.316.733.733 0 0 1-.031.893A8.349 8.349 0 0 1 8.344 16C3.734 16 0 12.286 0 7.71 0 4.266 2.114 1.312 5.124.06A.752.752 0 0 1 6 .278z"/></svg>
            }
          </button>
          <button className="selector-icon-btn" onClick={() => navigate('/account')} title="My account" aria-label="My account">
            <svg viewBox="0 0 16 16" width="15" height="15" fill="currentColor" aria-hidden="true"><circle cx="8" cy="5.5" r="2.8"/><path d="M2 14.5c0-3.3 2.7-6 6-6s6 2.7 6 6H2z"/></svg>
          </button>
          {userProfile?.role === 'admin' && (
            <button className="selector-icon-btn" onClick={() => navigate('/admin')} title="Admin console" aria-label="Admin console">
              <svg viewBox="0 0 16 16" width="15" height="15" fill="currentColor" aria-hidden="true"><path d="M8 4.754a3.246 3.246 0 1 0 0 6.492 3.246 3.246 0 0 0 0-6.492M5.754 8a2.246 2.246 0 1 1 4.492 0 2.246 2.246 0 0 1-4.492 0"/><path d="M9.796 1.343c-.527-1.79-3.065-1.79-3.592 0l-.094.319a.873.873 0 0 1-1.255.52l-.292-.16c-1.64-.892-3.433.902-2.54 2.541l.159.292a.873.873 0 0 1-.52 1.255l-.319.094c-1.79.527-1.79 3.065 0 3.592l.319.094a.873.873 0 0 1 .52 1.255l-.16.292c-.892 1.64.901 3.434 2.541 2.54l.292-.159a.873.873 0 0 1 1.255.52l.094.319c.527 1.79 3.065 1.79 3.592 0l.094-.319a.873.873 0 0 1 1.255-.52l.292.16c1.64.893 3.434-.902 2.54-2.541l-.159-.292a.873.873 0 0 1 .52-1.255l.319-.094c1.79-.527 1.79-3.065 0-3.592l-.319-.094a.873.873 0 0 1-.52-1.255l.16-.292c.893-1.64-.902-3.433-2.541-2.54l-.292.159a.873.873 0 0 1-1.255-.52zm-2.633.283c.246-.835 1.428-.835 1.674 0l.094.319a1.873 1.873 0 0 0 2.693 1.115l.291-.16c.764-.415 1.6.42 1.184 1.185l-.159.292a1.873 1.873 0 0 0 1.116 2.692l.318.094c.835.246.835 1.428 0 1.674l-.319.094a1.873 1.873 0 0 0-1.115 2.693l.16.291c.415.764-.42 1.6-1.185 1.184l-.291-.159a1.873 1.873 0 0 0-2.693 1.116l-.094.318c-.246.835-1.428.835-1.674 0l-.094-.319a1.873 1.873 0 0 0-2.692-1.115l-.292.16c-.764.415-1.6-.42-1.184-1.185l.159-.291A1.873 1.873 0 0 0 1.945 8.93l-.319-.094c-.835-.246-.835-1.428 0-1.674l.319-.094A1.873 1.873 0 0 0 3.06 4.377l-.16-.292c-.415-.764.42-1.6 1.185-1.184l.292.159a1.873 1.873 0 0 0 2.692-1.115z"/></svg>
            </button>
          )}
        </div>
      </header>

      <div className="selector-body">
        {pendingDeletion && <DeletionBanner pendingDeletion={pendingDeletion} onCancel={handleCancelDeletion} cancelLoading={cancelLoading} />}

        <h1 className="selector-greeting">
          {displayName ? `Hey, ${displayName}` : 'Your Leagues'}
        </h1>

        {error && <div className="selector-error">{error}</div>}

        <div className="selector-actions">
          <button className="selector-btn-primary" onClick={() => setShowCreate(true)}>
            + Create League
          </button>
          <button className="selector-btn-secondary" onClick={() => navigate('/directory')}>
            Browse Public Leagues
          </button>
        </div>

        {leagues && leagues.map((league) => (
          <LeagueCard
            key={league.id}
            league={league}
            uid={currentUser?.uid}
            rank={ranks[league.id] ?? null}
            onClick={() => navigate(`/league/${league.id}`)}
          />
        ))}

      </div>

      {showCreate && (
        <CreateLeagueModal
          onClose={() => {
            setShowCreate(false);
            setLeagues(null);
            setRanks({});
            loadLeagues();
          }}
        />
      )}

    </div>
  );
}

/* ── Sub-components ─────────────────────────────────────────── */

function DeletionBanner({ pendingDeletion, onCancel, cancelLoading }) {
  const executeAt = pendingDeletion?.executeAt;
  const dateStr = executeAt
    ? new Date(executeAt.seconds ? executeAt.seconds * 1000 : executeAt).toLocaleString('en-US', {
        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
      })
    : '';

  return (
    <div className="selector-deletion-banner">
      <div className="selector-deletion-banner-text">
        <strong>Account deletion scheduled</strong>
        {dateStr && ` — your account and league(s) will be permanently deleted on ${dateStr}.`}
        {' '}You can cancel before then.
      </div>
      <button
        className="selector-deletion-cancel-btn"
        onClick={onCancel}
        disabled={cancelLoading}
      >
        {cancelLoading ? 'Cancelling…' : 'Cancel Deletion'}
      </button>
    </div>
  );
}


function LeagueCard({ league, uid, rank, onClick }) {
  const stats = league.memberStats;
  const isCommissioner = uid === league.commissionerId;
  const points = stats?.totalPoints ?? 0;

  return (
    <div
      className="selector-league-card"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
    >
      <div className="slc-top">
        <span className="slc-name">{league.name}</span>
        <span className="slc-badge">
          {isCommissioner ? 'Commissioner' : (league.isPublic ? 'Public' : 'Private')}
        </span>
      </div>
      <div className="slc-meta">
        {rank !== null && (
          <>
            <span className="slc-rank">#{rank}</span>
            <span className="slc-dot">·</span>
          </>
        )}
        <span>{points} pts</span>
        <span className="slc-dot">·</span>
        <span>{league.memberCount} member{league.memberCount !== 1 ? 's' : ''}</span>
      </div>
    </div>
  );
}
